/**
 * Asset Form Configuration
 * 
 * Central configuration file for the Crown Jewel Asset form.
 * Defines all sections and fields, making it easy to add/modify
 * fields without changing component code.
 * 
 * Updated for PRD v8: Risk Sources Classification Update
 */

// Asset types available for selection
export const assetTypes = [
  "Application",
  "Database",
  "Server",
  "Network Device",
  "Endpoint",
  "Data",
  "Cloud",
  "Other"
];

// Risk sources classification levels (PRD v8 update)
export const riskSourcesLevels = [
  "Internal",
  "External",
  "Both"
];

// Criticality classification levels (numbered scale)
export const criticalityLevels = [
  "1-Extremely",
  "2-Very High",
  "3-High",
  "4-Medium",
  "5-Low"
];

// Sensitive data classification options (consolidated from multiple fields)
export const sensitiveDataTypes = [
  "None",
  "Customer PII",
  "Employee PII",
  "Financial Information",
  "Executive Information",
  "Multiple Types"
];

// Form tab configuration
export const formTabs = [
  { id: "basic", label: "Basic Information" },
  { id: "security", label: "Risk Sources" },
  { id: "data", label: "Data Classification" },
  { id: "controls", label: "Key Controls" },
  { id: "comments", label: "Comments" }
];

// Form configuration by section
export const formSections = {
  basicInfo: {
    title: "Basic Information",
    tab: "basic",
    fields: [
      { 
        name: "name", 
        type: "text", 
        label: "Application/System Name", 
        required: true,
        placeholder: "Name of the application or system"
      },
      { 
        name: "description", 
        type: "textarea", 
        label: "Description",
        placeholder: "Brief description of the asset",
        rows: 3
      },
      { 
        name: "location", 
        type: "text", 
        label: "Location",
        placeholder: "Physical or logical location" 
      }
    ]
  },
  ownerInfo: {
    title: "Owner Information",
    tab: "basic",
    fields: [
      { 
        name: "ownerDepartment", 
        type: "text", 
        label: "Asset Owner Department",
        placeholder: "Department or business unit",
        required: true
      },
      { 
        name: "ownerName", 
        type: "text", 
        label: "Asset Owner Name",
        placeholder: "Business owner's name",
        required: true
      },
      { 
        name: "ownerEmail", 
        type: "email", 
        label: "E-mail",
        placeholder: "Contact email for the owner",
        required: true
      }
    ]
  },
  securityInfo: {
    title: "Risk Sources",
    tab: "security",
    fields: [
      { 
        name: "riskSources", 
        type: "select", 
        label: "Risk Sources", 
        options: riskSourcesLevels,
        required: true,
        helpText: "Identify whether risks primarily originate from internal, external, or both sources"
      },
      { 
        name: "criticalityClassification", 
        type: "select", 
        label: "Criticality Classification", 
        options: criticalityLevels,
        required: true
      }
    ]
  },
  dataInfo: {
    title: "Data Classification",
    tab: "data",
    fields: [
      {
        name: "containsSensitiveData",
        type: "select",
        label: "Contains Sensitive Data",
        options: sensitiveDataTypes,
        required: true,
        helpText: "Select the primary type of sensitive information this asset contains"
      }
    ]
  },
  keyControls: {
    title: "Key Controls Implementation",
    tab: "controls",
    fields: [
      {
        name: "ssoImplemented",
        type: "select",
        label: "SSO",
        options: ["Yes", "No", "N/A"],
        required: true
      },
      {
        name: "twoFactorAuth",
        type: "select",
        label: "2FA",
        options: ["Yes", "No", "N/A"],
        required: true
      },
      {
        name: "dataEncryption",
        type: "select",
        label: "Data Encryption",
        options: ["Yes", "No", "N/A"],
        required: true
      },
      {
        name: "backupsImplemented",
        type: "select",
        label: "Backups",
        options: ["Yes", "No", "N/A"],
        required: true
      },
      {
        name: "networkSegmentation",
        type: "select",
        label: "Network Segmentation",
        options: ["Yes", "No", "N/A"],
        required: true
      },
      {
        name: "userAccessReviews",
        type: "select",
        label: "User Access Reviews",
        options: ["Yes", "No", "N/A"],
        required: true
      }
    ]
  },
  comments: {
    title: "Asset Comments",
    tab: "comments",
    fields: [
      {
        name: "generalComments",
        type: "textarea",
        label: "General Comments",
        placeholder: "Add your comments about this asset here...\n\nExamples:\n• Important configuration details\n• Known issues or limitations\n• Contact information for support\n• Historical context or changes\n• Special requirements or considerations",
        rows: 12,
        helpText: "Add general comments, observations, or notes about this asset. Comments are automatically saved as you type."
      }
    ]
  }
};

// Default values for new assets - updated for Risk Sources
export const defaultAssetValues = {
  basicInfo: {
    name: '',
    description: '',
    location: ''
  },
  ownerInfo: {
    ownerDepartment: '',
    ownerName: '',
    ownerEmail: ''
  },
  securityInfo: {
    riskSources: 'Internal',
    criticalityClassification: '4-Medium'
  },
  keyControls: {
    ssoImplemented: 'No',
    twoFactorAuth: 'No',
    dataEncryption: 'No',
    backupsImplemented: 'No',
    networkSegmentation: 'No',
    userAccessReviews: 'No'
  },
  dataInfo: {
    containsSensitiveData: 'None'
  },
  comments: {
    generalComments: '',
    lastUpdated: null,
    updatedBy: null,
    commentHistory: []
  }
}; 